import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquareText, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
    let id = localStorage.getItem("om_chat_session");
    if (!id) {
        id = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem("om_chat_session", id);
    }
    return id;
};

const SUGGESTIONS = [
    "What is a 7% Kisan Kota plot?",
    "Kisan Kota plot price in Sector 18?",
    "Is PTL mandatory before registry?",
];

const ChatWidget = () => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: "assistant", text: "Namaste! I'm the OM Infra AI advisor. Ask me anything about YEIDA Authority plots, 7% Kisan Kota plots, PTL, TM or registry." },
    ]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const bottomRef = useRef(null);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, open]);

    const send = async (text) => {
        const msg = (text || input).trim();
        if (!msg || busy) return;
        setInput("");
        setBusy(true);
        setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
        try {
            const resp = await fetch(`${API}/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: getSessionId(), message: msg }),
            });
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split("\n\n");
                buffer = parts.pop();
                for (const part of parts) {
                    if (!part.startsWith("data: ")) continue;
                    const payload = part.slice(6);
                    if (payload === "[DONE]") break;
                    try {
                        const { t } = JSON.parse(payload);
                        if (t) {
                            setMessages((m) => {
                                const copy = [...m];
                                copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + t };
                                return copy;
                            });
                        }
                    } catch { /* partial chunk */ }
                }
            }
        } catch {
            setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: "Connection issue — please call or WhatsApp us at +91 93113 05205." };
                return copy;
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <motion.button
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 1.5, type: "spring" }}
                onClick={() => setOpen(!open)}
                data-testid="ai-chat-toggle"
                aria-label="Ask OM Infra AI advisor"
                className="fixed bottom-[76px] left-4 md:bottom-5 md:left-5 z-[90] w-14 h-14 rounded-full bg-navy border-2 border-gold shadow-2xl flex items-center justify-center hover:bg-navy-800 transition-colors"
            >
                {open ? <X className="w-6 h-6 text-gold" /> : <MessageSquareText className="w-6 h-6 text-gold" />}
            </motion.button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 24, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.96 }}
                        className="fixed bottom-[140px] md:bottom-24 left-4 sm:left-5 z-[90] w-[calc(100vw-2rem)] sm:w-96 bg-white rounded-lg shadow-2xl border border-slate-200 overflow-hidden flex flex-col"
                        style={{ maxHeight: "min(560px, 70vh)" }}
                        data-testid="ai-chat-panel"
                    >
                        <div className="bg-navy px-4 py-3.5 flex items-center gap-3">
                            <span className="w-9 h-9 rounded-full bg-gold flex items-center justify-center font-serif font-bold text-navy text-sm">OM</span>
                            <div>
                                <p className="text-white font-semibold text-sm">OM Infra AI Advisor</p>
                                <p className="text-[10px] font-mono uppercase tracking-widest text-gold">Powered by Claude</p>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-bone" data-testid="ai-chat-messages">
                            {messages.map((m, i) => (
                                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                    <div className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-700"}`}>
                                        {m.text || (busy && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin text-gold-deep" /> : "")}
                                    </div>
                                </div>
                            ))}
                            <div ref={bottomRef} />
                        </div>
                        {messages.length === 1 && (
                            <div className="px-3 pb-2 bg-bone flex flex-wrap gap-1.5">
                                {SUGGESTIONS.map((s) => (
                                    <button key={s} onClick={() => send(s)} data-testid={`chat-suggestion-${s.slice(0, 12).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} className="text-[11px] bg-white border border-gold/40 text-navy px-2.5 py-1.5 rounded-full hover:bg-gold-soft transition-colors">
                                        {s}
                                    </button>
                                ))}
                            </div>
                        )}
                        <form
                            onSubmit={(e) => { e.preventDefault(); send(); }}
                            className="p-3 bg-white border-t border-slate-200 flex gap-2"
                        >
                            <input
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Ask about plots, PTL, prices..."
                                className="flex-1 bg-bone border border-slate-200 rounded-sm px-3 py-2.5 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-gold/60"
                                data-testid="ai-chat-input"
                            />
                            <button type="submit" disabled={busy} data-testid="ai-chat-send" aria-label="Send message" className="bg-gold text-navy px-3.5 rounded-sm hover:bg-gold-bright transition-colors disabled:opacity-60">
                                <Send className="w-4 h-4" />
                            </button>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default ChatWidget;
